"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Separator } from "./ui/separator";
import { Badge } from "./ui/badge";
import { 
  FileText, 
  User, 
  GraduationCap, 
  CreditCard, 
  MapPin, 
  Briefcase, 
  Award,
  CheckCircle,
  ArrowLeft,
  Send,
  Eye,
  Calendar,
  Clock,
  FileCheck
} from "lucide-react";
import openTurfLogo from '../assets/OpenTurf-logo.png';

interface DocumentReviewProps {
  onComplete: () => void;
  onShowTerms?: () => void;
  onBack: () => void;
}

// Mock uploaded document data
const uploadedDocuments = [
  {
    id: "identity",
    title: "Identity Verification",
    type: "Aadhaar Card",
    icon: User,
    status: "verified",
    uploadedAt: "2025-01-14T10:30:00Z",
    fileSize: "2.4 MB",
    fileName: "aadhaar_front_back.pdf"
  },
  {
    id: "resume", 
    title: "Resume/CV",
    type: "Professional Resume",
    icon: FileText,
    status: "verified",
    uploadedAt: "2025-01-14T10:32:00Z",
    fileSize: "1.8 MB",
    fileName: "john_doe_resume.pdf"
  },
  {
    id: "education",
    title: "Educational Certificates",
    type: "Degree Certificate",
    icon: GraduationCap,
    status: "verified", 
    uploadedAt: "2025-01-14T10:35:00Z",
    fileSize: "3.2 MB",
    fileName: "degree_certificate.pdf"
  },
  {
    id: "pan",
    title: "PAN Card",
    type: "Income Tax PAN",
    icon: CreditCard,
    status: "verified",
    uploadedAt: "2025-01-14T10:37:00Z",
    fileSize: "1.1 MB", 
    fileName: "pan_card.pdf"
  },
  {
    id: "address",
    title: "Address Proof", 
    type: "Utility Bill",
    icon: MapPin,
    status: "verified",
    uploadedAt: "2025-01-14T10:39:00Z",
    fileSize: "2.7 MB",
    fileName: "electricity_bill.pdf"
  },
  {
    id: "experience",
    title: "Experience Letters",
    type: "Previous Employment",
    icon: Briefcase,
    status: "verified",
    uploadedAt: "2025-01-14T10:42:00Z",
    fileSize: "4.1 MB",
    fileName: "experience_letters.pdf"
  }
];

export function DocumentReview({ onComplete, onShowTerms, onBack }: DocumentReviewProps) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    onComplete();
  };

  const formatFileSize = (sizeInMB: string) => {
    return sizeInMB;
  };

  const formatUploadTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const totalDocuments = uploadedDocuments.length;
  const verifiedDocuments = uploadedDocuments.filter(doc => doc.status === 'verified').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-blue-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Separator orientation="vertical" className="h-6" />
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FileCheck className="w-5 h-5 text-blue-600" />
              <span className="text-gray-900">Step 3 of 3</span>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-6">
              <FileCheck className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Review Your Documents
            </h1>
            
            <p className="text-gray-600 max-w-2xl mx-auto">
              Please review all uploaded documents before final submission. Make sure all information is accurate and files are properly uploaded.
            </p>
          </motion.div>

          {/* Summary Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
          >
            <Card className="bg-white/80 backdrop-blur-sm border-white/20 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-gray-900">{totalDocuments}</h3>
                  <p className="text-sm text-gray-600">Total Documents</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-white/20 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-gray-900">{verifiedDocuments}</h3>
                  <p className="text-sm text-gray-600">Verified</p>
                </div>
              </div>
            </Card>

            <Card className="bg-white/80 backdrop-blur-sm border-white/20 p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-gray-900">Ready</h3>
                  <p className="text-sm text-gray-600">For Submission</p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Document List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4 mb-8"
          >
            <h2 className="text-gray-900 mb-6">Uploaded Documents</h2>
            
            {uploadedDocuments.map((document, index) => (
              <motion.div
                key={document.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
              >
                <Card className="bg-white/80 backdrop-blur-sm border-white/20 p-6 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                        <document.icon className="w-6 h-6 text-gray-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="text-gray-900">{document.title}</h4>
                          <Badge 
                            variant="secondary" 
                            className="bg-green-100 text-green-700 border-green-200"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center space-x-1">
                            <FileText className="w-4 h-4" />
                            <span>{document.fileName}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Eye className="w-4 h-4" />
                            <span>{document.fileSize}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Clock className="w-4 h-4" />
                            <span>{formatUploadTime(document.uploadedAt)}</span>
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-500 mt-1">
                          {document.type}
                        </p>
                      </div>
                    </div>
                    
                    <Button variant="outline" size="sm" className="flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>Preview</span>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Submission Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                <FileCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="text-blue-900 mb-2">Ready for Submission</h4>
                <p className="text-blue-700 text-sm leading-relaxed">
                  All your documents have been successfully uploaded and verified. Once you submit, 
                  our HR team will review your application and get back to you within 2-3 business days.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex items-center justify-between"
          >
            <Button
              variant="outline"
              onClick={onBack}
              className="px-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documents
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 hover:opacity-90 transition-opacity"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Documents
                </>
              )}
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}